import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, Box, CircularProgress, TextField, Tooltip } from '@mui/material';
import useSupplierFilters from '../../resources/suppliers/components/SupplierFilterHelper';
import { useGetSuppliersHandler } from '../../resources/suppliers/dataHandlers/getSuppliersHandler';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import { getFieldErrorMessage } from '../../utils/apiFieldErrors';

const hasVatNumber = (supplier) => {
  const vatNumber = supplier?.party_tax_scheme?.company_id;

  return typeof vatNumber === 'string' && vatNumber.trim() !== '';
};

const getSupplierVatIdentifier = (supplier) => supplier?.party_tax_scheme?.company_id || supplier?.company_id || '';

const getSupplierOptionLabel = (option) => {
  const name = option?.name || '';
  const email = option?.email || '';
  const identifier = getSupplierVatIdentifier(option);

  return [name, email, identifier].filter(Boolean).join(' | ');
};

const SupplierProfileSelect = ({
  label = 'Billed by',
  name,
  control,
  errors,
  readOnly = false,
  requireVat = true,
  onSupplierLoaded,
}) => {
  const { validations } = useTranslatedMessages();
  const vatRequiredMessage =
    validations?.invoice?.supplier_vat_required_for_purchase ??
    'Purchase documents require a supplier with a VAT number';

  const getDisabledReason = (supplier) => {
    if (requireVat && !hasVatNumber(supplier)) {
      return vatRequiredMessage;
    }

    return '';
  };

  const { currentQuery, handleSearchChange } = useSupplierFilters({ defaultQuery: {}, searchOnChange: true });

  const { suppliers, isLoading } = useGetSuppliersHandler({ searchQuery: currentQuery });
  const errorMessage = getFieldErrorMessage(errors, name);

  useEffect(() => {
    if (suppliers) {
      onSupplierLoaded?.(suppliers);
    }
  }, [onSupplierLoaded, suppliers]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Autocomplete
            loading={isLoading}
            id={name}
            fullWidth
            readOnly={readOnly}
            options={suppliers || []}
            getOptionLabel={getSupplierOptionLabel}
            getOptionKey={(option) => option?.id}
            getOptionDisabled={(option) => Boolean(getDisabledReason(option))}
            renderOption={(props, option) => {
              const { key, onClick, onMouseDown, ...optionProps } = props;
              const disabledReason = getDisabledReason(option);
              const blockDisabledSelection = (event) => {
                event.preventDefault();
                event.stopPropagation();
              };

              return (
                <Box
                  key={key}
                  component="li"
                  {...optionProps}
                  onMouseDown={disabledReason ? blockDisabledSelection : onMouseDown}
                  onClick={disabledReason ? blockDisabledSelection : onClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    opacity: disabledReason ? 0.5 : 1,
                    cursor: disabledReason ? 'not-allowed' : 'pointer',
                  }}
                >
                  {disabledReason ? (
                    <Tooltip title={disabledReason} placement="right">
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'auto',
                        }}
                      >
                        {getSupplierOptionLabel(option)}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getSupplierOptionLabel(option)}
                    </Box>
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!errorMessage}
                helperText={errorMessage}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => {
              if (value && getDisabledReason(value)) {
                return;
              }

              field.onChange(value);
            }}
            onBlur={field.onBlur}
            name={field.name}
            ref={field.ref}
            value={field.value || null}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                handleSearchChange(event);
              }
            }}
            filterOptions={(x) => x}
          />
        </Box>
      )}
    />
  );
};

export default SupplierProfileSelect;
